import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Mail, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useWeeklyUpdateRecipients } from "@/hooks/useWeeklyUpdate";
import { cn } from "@/lib/cn";

/**
 * "Send to…" for the Weekly Update (Brett, 2026-09-22: "i may want to
 * email it to more than just one person"). Recipients are chips: pick
 * an active planner user from the suggestions, or type any address —
 * board members needn't have a login. The list the user sends to is
 * remembered on their profile and prefilled next week (the host page
 * owns that PATCH). The scheduled Monday send is untouched: it still
 * goes to Brett alone, and this dialog is the reviewed "publish" step.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(s: string): boolean {
  return EMAIL_RE.test(s.trim());
}

export function SendDialog({
  open,
  onOpenChange,
  initial,
  subject,
  sending,
  onSend,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefilled recipients (the user's remembered list). */
  initial: string[];
  subject: string;
  sending: boolean;
  onSend: (to: string[]) => void;
}) {
  const users = useWeeklyUpdateRecipients(open);
  const [to, setTo] = useState<string[]>(initial);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed from the remembered list each time the dialog opens.
  useEffect(() => {
    if (open) {
      setTo(initial);
      setDraft("");
      setError(null);
    }
  }, [open, initial]);

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const chosen = new Set(to.map((e) => e.toLowerCase()));
    return (users.data ?? [])
      .filter((u) => !chosen.has(u.email.toLowerCase()))
      .filter((u) => !q || u.email.toLowerCase().includes(q) || (u.display_name ?? "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [users.data, draft, to]);

  const add = (raw: string): boolean => {
    const email = raw.trim().replace(/[,;]$/, "").toLowerCase();
    if (!email) return false;
    if (!isValidEmail(email)) {
      setError(`"${raw.trim()}" isn't an email address`);
      return false;
    }
    setTo((prev) => (prev.some((e) => e.toLowerCase() === email) ? prev : [...prev, email]));
    setDraft("");
    setError(null);
    return true;
  };

  // Send with text still in the box: a valid address joins the list, an
  // invalid one blocks the send (never silently dropped either way).
  const submit = () => {
    const pending = draft.trim().replace(/[,;]$/, "").toLowerCase();
    if (pending) {
      if (!add(pending)) return;
      onSend(to.some((e) => e.toLowerCase() === pending) ? to : [...to, pending]);
      return;
    }
    onSend(to);
  };

  const remove = (email: string) => setTo((prev) => prev.filter((e) => e !== email));

  const nameFor = (email: string) =>
    users.data?.find((u) => u.email.toLowerCase() === email.toLowerCase())?.display_name ?? null;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        add(draft);
      }
    } else if (e.key === "Backspace" && !draft && to.length > 0) {
      remove(to[to.length - 1]);
    }
  };

  const canSend = to.length > 0 && !sending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle>Send the weekly update</DialogTitle>
          <DialogDescription className="truncate">{subject}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label htmlFor="wu-recipients" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            To
          </label>
          <div
            className={cn(
              "flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-lg border bg-surface px-2 py-1.5",
              error ? "border-red-300 dark:border-red-500/40" : "border-line focus-within:border-brand-navy-400 focus-within:ring-1 focus-within:ring-brand-navy-400",
            )}
            onClick={() => inputRef.current?.focus()}
            data-testid="recipient-chips"
          >
            {to.map((email) => {
              const name = nameFor(email);
              return (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-full bg-raised py-0.5 pl-2.5 pr-1 text-xs text-ink"
                  data-testid="recipient-chip"
                  title={email}
                >
                  {name ? <span className="font-medium">{name}</span> : null}
                  <span className={cn(name && "text-ink-muted")}>{name ? `<${email}>` : email}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(email);
                    }}
                    className="rounded-full p-0.5 text-ink-subtle hover:bg-muted hover:text-ink-2"
                    aria-label={`Remove ${email}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
            <input
              ref={inputRef}
              id="wu-recipients"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={onKeyDown}
              onBlur={() => {
                if (draft.trim()) add(draft);
              }}
              placeholder={to.length ? "Add another…" : "Type a name or email address"}
              className="min-w-[160px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm placeholder:text-ink-subtle focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {error && <div className="text-xs text-red-700 dark:text-red-300" data-testid="recipient-error">{error}</div>}

          {suggestions.length > 0 && (
            <ul className="max-h-48 overflow-y-auto rounded-lg border border-line bg-surface py-1 text-sm shadow-sm" data-testid="recipient-suggestions">
              {suggestions.map((u) => (
                <li key={u.email}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => add(u.email)}
                    className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left hover:bg-raised"
                  >
                    <span className="font-medium text-ink">{u.display_name ?? u.email}</span>
                    {u.display_name && <span className="text-xs text-ink-muted">{u.email}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-ink-muted">
            Pick planner users from the list, or type any address and press Enter. This list is remembered for next week.
          </p>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSend && !draft.trim()}
            className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
            data-testid="send-confirm"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
            {to.length === 0 ? "Send" : to.length === 1 ? "Send to 1 person" : `Send to ${to.length} people`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
